import { Box, Button, Flex, Input, Select, SimpleGrid, Switch, Text, Textarea, useToast, AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, useDisclosure } from '@chakra-ui/react';
import { quickCashflowAssistApi } from 'api/quickCashflowAssistApi';
import { getCustomerCockpitProfileApi } from 'api/customerCockpitProfileApi';
import {
  getFinancialSnapshotVersionApi,
  listFinancialSnapshotVersionsApi,
} from 'api/financialSnapshotVersionsApi';
import { HelperTooltip } from 'components/Tooltip';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  applyAssistResponse,
  buildAssistChatText,
  formatAssistTokenUsage,
} from '../QuickCashflowModel/applyAssistResponse';
import { buildMenuActionsFromSession } from '../QuickCashflowModel/buildMenuActions';
import { resolveExtractPollPayload, applyExtractPollTerminal } from '../QuickCashflowModel/resolveExtractPollPayload';
import { buildAutoFixFeedback } from '../QuickCashflowModel/calc/buildAutoFixFeedback';
import { buildHillSpendRoasVectors } from '../QuickCashflowModel/calc/applyHillRoasEfficiencyRatio';
import {
  diagnoseHillFit,
  fitHillRoasModel,
  HILL_FIT_NO_SATURATION,
  hasCohortSheet,
} from '../QuickCashflowModel/calc/roasSaturation';
import CopyRoasVectorButton from '../QuickCashflowModel/CopyRoasVectorButton';
import PercentInput from '../QuickCashflowModel/PercentInput';
import DecimalInput from '../QuickCashflowModel/DecimalInput';
import HillMaxRoasSection from '../QuickCashflowModel/HillMaxRoasSection';
import FutureFundingRoasSection from '../QuickCashflowModel/FutureFundingRoasSection';
import MarketingPlanMonthsRow from '../QuickCashflowModel/MarketingPlanMonthsRow';
import { fmtIntegerInput, parseIntegerInput, parsePercentInput } from '../QuickCashflowModel/calc/formatters';
import { getCohortSummary } from '../QuickCashflowModel/calc/cohortSheetSummary';
import {
  calcRunwayFromNetBurn,
  calcLiquidValueOpexMultiple,
  calcCashOpexMultiple,
  calcBalanceSheetRatio,
  isManualUwSource,
  roundRunwayMonths,
} from '../QuickCashflowModel/calc/underwritingSheetMetrics';
import {
  maxSpendForTargetRoasHill,
  resolveLatestCohortMarketingSpend,
  resolveMaxRoas100TargetMob,
  resolveUaPredictionInputs,
  UA_PREDICTION_TARGET_ROAS,
} from '../QuickCashflowModel/calc/uaPrediction';
import { resolveUseHillRoasForFunding, FUTURE_FUNDING_ROAS_SOURCE } from '../QuickCashflowModel/calc/resolveUseHillRoasForFunding';
import { marketingPlanMonthLabels } from '../QuickCashflowModel/calc/monthLabel';
import {
  applyCohortAnchoredPlanStart,
  isResultsCalendarAligned,
} from '../QuickCashflowModel/calc/planStartMonth';
import {
  applyPastedMarketingSpend,
  parseMarketingSpendPaste,
} from '../QuickCashflowModel/calc/pasteMarketingSpend';
import {
  createEmptyModel,
  createDefaultAssistSession,
  DEFAULT_FUNDING_PCT,
  DEFAULT_MAX_EXPOSURE,
  FACILITY_SIZE_TOOLTIP,
  DEFAULT_ROAS,
  DEFAULTS,
  MAX_FACILITY_FEE_INSTALLMENTS,
  MAX_PLAN_MARKETING_MONTHS,
} from '../QuickCashflowModel/constants';
import {
  ensureMarketingPlanIncludesCurrentMonth,
  disableMarketingPlanManualMode,
  padPlanArrays,
} from '../QuickCashflowModel/normalizeMarketingPlan';
import AssistResultsPanel from '../QuickCashflowModel/AssistResultsPanel';
import { snapshotVersionLabel } from '../QuickCashflowModel/FinancialSnapshotData/financialSnapshotDraft';
import { enrichAccountingSnapshot, syncAccountingSnapshotKpis } from '../QuickCashflowModel/enrichAccountingSnapshot';
import { hydrateModelFromFinancialSnapshot } from '../QuickCashflowModel/hydrateModelFromFinancialSnapshot';
import {
  cancelQuickCashflowExtractJob,
  deleteQuickCashflowModel,
  deleteQuickCashflowModelOpenAIFiles,
  getQuickCashflowExtractStatus,
  getQuickCashflowModel,
  listQuickCashflowModels,
  saveQuickCashflowModel,
} from 'api/quickCashflowModelApi';
import { getLatestScorecardConfig } from 'api/tearsheetScorecardConfigApi';
import CohortSheetTable from '../QuickCashflowModel/CohortSheetTable';
import DownloadExcelButton from '../QuickCashflowModel/DownloadExcelButton';
import { buildCohortExportOptionsFromState } from '../QuickCashflowModel/calc/buildCohortExportOptions';
import { downloadCohortSheetExcel } from '../QuickCashflowModel/calc/exportCohortSheetExcel';
import { downloadQuickCashflowExcel } from '../QuickCashflowModel/calc/exportQuickCashflowExcel';
import { exportQuickCashflowToDrive } from '../QuickCashflowModel/calc/exportQuickCashflowToDrive';
import { quickCashflowDriveExportSuccessToast } from '../QuickCashflowModel/calc/quickCashflowDriveExportToast';
import { resolveSaveOverwriteTarget } from '../QuickCashflowModel/calc/resolveSaveOverwriteTarget';
import { hydrateCalcResult } from '../QuickCashflowModel/calc/hydrateCalcResult';
import { resolveExposureLimitsForOverallGrade, formatOverallGradeDisplay } from '../QuickCashflowModel/calc/exposureGradeLimits';
import { stateHasDraws } from '../QuickCashflowModel/calc/constraintViolations';
import {
  resolveFundingCommitmentMonths,
} from '../QuickCashflowModel/calc/resolveFundingCommitment';
import {
  resolveFacilityFeeAmount,
  resolveFacilitySize,
  resolveRequiredMaxExposure,
  stateWithMaxExposure,
} from '../QuickCashflowModel/calc/resolvePlanCovenantLimits';
import { resolveFirstMarketingMonth } from '../QuickCashflowModel/calc/resolveDealIrrModelInputs';
import {
  PRO_RATA_REV_SHARE_TOOLTIP,
  resolveCohortDraw,
  resolveDefaultRevSharePct,
  seedPlanDrawAmountFromFundingPct,
} from '../QuickCashflowModel/calc/resolvePlanFunding';
import {
  buildUwScoringRows,
  computeUwScorecardTotals,
} from '../QuickCashflowModel/calc/underwritingScorecardScoring';
import FacilityFeeTypeRow from '../QuickCashflowModel/FacilityFeeTypeRow';
import FacilityGracePeriodsRow from '../QuickCashflowModel/FacilityGracePeriodsRow';
import UnderwritingFinalScoreSummary from '../QuickCashflowModel/UnderwritingFinalScoreSummary';
import ScenarioSettingsSection from '../QuickCashflowModel/ScenarioSettingsSection';
import ScenarioResultsSection from '../QuickCashflowModel/ScenarioResultsSection';
import { runScenarios } from '../QuickCashflowModel/calc/runScenarios';
import '../QuickCashflowModel/quickCashflow.scss';
import { CockpitCustomerContext } from 'context/CockpitCustomerContext';

function formatModelSavedDate(value) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function savedModelLabel(model) {
  const savedOn = formatModelSavedDate(model.createdAt || model.updatedAt);
  return savedOn ? `${model.name} (${savedOn})` : model.name;
}

const ROAS_VECTOR_TOOLTIP = (
  <>
    <Text mb={2}>
      This curve is derived from the cohort table below. Once the cohort triangle is
      complete (missing cells Markov-filled from older cohorts), we average the
      cumulative ROAS at each month-on-book (MOB) across the most recent three cohorts.
    </Text>
    <Text>
      MOB 0 is the average first-month ROAS of those three cohorts, MOB 1 their average
      cumulative ROAS after two months, and so on. The result is editable here and is
      used for new marketing whenever spend-based Hill saturation cannot be fit.
    </Text>
  </>
);

const NUMBER_OF_REPAYMENTS_TOOLTIP = (
  <>
    <Text mb={2}>
      Repayment periods per cohort (after grace). When blank, defaults to the cumulative
      ROAS vector length (see note below the field).
    </Text>
    <Text mb={2}>
      If this count is <strong>longer</strong> than the ROAS vector and a funded cohort
      would not fully repay within the raw vector (and has not reached 100% cumulative
      ROAS), the calculator extends the vector at calculate time only — your stored ROAS
      curve is unchanged. Extension repeats the last marginal ROAS step for extra periods,
      then may raise the final period so principal and accrued fees clear within the
      repayment window.
    </Text>
    <Text>
      No extension is applied when the cohort is unfunded, already repays within the raw
      vector, or cumulative ROAS is already 100% or higher.
    </Text>
  </>
);

const MANUAL_PLAN_MODE_TOOLTIP =
  'When off, every row uses the top defaults for funding %, revenue share %, and covenants. When on, set funding ($), revenue share %, and max exposure per row. Max monthly draw and max draw increase are ignored in manual mode.';

const AUTO_FIX_FUNDING_TOOLTIP =
  'When on, Calculate automatically adjusts funding % and revenue share to stay within max exposure, max monthly draw, and max draw increase. Run rate and runoff grade exposure violations are highlighted in the results table but are not auto-fixed.';

function shouldAutoSyncMarketingKpiField(source, currentValue, nextValue) {
  if (source !== 'Manual entry') {
    return true;
  }
  return currentValue === 0 && nextValue > 0;
}

function SourceBadge({ label }) {
  const isDefault =
    label === 'Default' || label === 'Manual' || label === 'Manual entry';
  return (
    <span
      className={`source-badge ${isDefault ? 'source-badge-default' : ''}`}
    >
      {label}
    </span>
  );
}

function QuickCashflowModelClassic() {
  const toast = useToast();
  const resultsRef = useRef(null);
  const { customerId } = useParams();
  const { customer } = useContext(CockpitCustomerContext);

  const [state, setState] = useState(() => createEmptyModel());
  const [snapshotHydrated, setSnapshotHydrated] = useState(false);
  const [snapshotVersions, setSnapshotVersions] = useState([]);
  const [selectedSnapshotVersionId, setSelectedSnapshotVersionId] = useState('');
  const [savedModels, setSavedModels] = useState([]);
  const [loadId, setLoadId] = useState('');
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [assistChatKey, setAssistChatKey] = useState(0);
  const [calcResult, setCalcResult] = useState(null);
  const [scenarioResults, setScenarioResults] = useState(null);
  const [calcTimestamp, setCalcTimestamp] = useState(null);
  const [resultsVisible, setResultsVisible] = useState(false);
  const [incomeExpanded, setIncomeExpanded] = useState(false);
  const [cohortAnalysisExpanded, setCohortAnalysisExpanded] = useState(false);
  const [companyScoreKpisExpanded, setCompanyScoreKpisExpanded] = useState(false);
  const [marketingPlanExpanded, setMarketingPlanExpanded] = useState(true);
  const [calcStatus, setCalcStatus] = useState(
    'Ready — edit inputs and press Calculate.'
  );
  const [calculating, setCalculating] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [deletingOpenAIFiles, setDeletingOpenAIFiles] = useState(false);
  const [deletingModel, setDeletingModel] = useState(false);
  const [extractUsage, setExtractUsage] = useState(null);
  const [extractChatAppend, setExtractChatAppend] = useState(null);
  const [cancellingExtract, setCancellingExtract] = useState(false);
  const [scorecardConfig, setScorecardConfig] = useState(null);
  const extractPollRef = useRef(null);
  const saveCancelRef = useRef(null);
  const {
    isOpen: isSaveConfirmOpen,
    onOpen: openSaveConfirm,
    onClose: closeSaveConfirm,
  } = useDisclosure();
  const [saveOverwriteTarget, setSaveOverwriteTarget] = useState('');
  const assistSessionRef = useRef(createDefaultAssistSession());
  const cohortSheetRef = useRef(null);

  useEffect(() => {
    assistSessionRef.current = state.assistSession ?? createDefaultAssistSession();
  }, [state.assistSession]);

  useEffect(() => {
    cohortSheetRef.current = state.cohortSheet ?? null;
  }, [state.cohortSheet]);

  useEffect(() => {
    getLatestScorecardConfig()
      .then(setScorecardConfig)
      .catch(() => setScorecardConfig(null));
  }, []);

  useEffect(() => {
    const snap = state.accountingSnapshot;
    if (!snap?.balanceSheet) {
      return;
    }
    const needsEnrich =
      snap.totalAssets?.value == null ||
      snap.totalLiabilities?.value == null ||
      snap.safe?.value == null ||
      snap.debt?.value == null;
    if (!needsEnrich) {
      return;
    }
    const enriched = enrichAccountingSnapshot(snap);
    if (
      enriched.totalAssets?.value === snap.totalAssets?.value &&
      enriched.totalLiabilities?.value === snap.totalLiabilities?.value &&
      enriched.safe?.value === snap.safe?.value &&
      enriched.debt?.value === snap.debt?.value
    ) {
      return;
    }
    setState((s) => ({
      ...s,
      accountingSnapshot: enriched,
    }));
  }, [state.accountingSnapshot]);

  const QCF_WELCOME_MESSAGES = useMemo(
    () => [
      {
        role: 'ai',
        text:
          'Welcome! Choose **Cohort**, **Accounting**, or **Marketing** to get started.',
      },
    ],
    []
  );

  const assistMenu = useMemo(
    () => buildMenuActionsFromSession(state.assistSession),
    [state.assistSession]
  );

  const refreshModels = useCallback(async () => {
    if (!customerId) {
      setSavedModels([]);
      return;
    }
    try {
      const rows = await listQuickCashflowModels(customerId);
      setSavedModels(rows || []);
    } catch {
      // ignore on first load
    }
  }, [customerId]);

  useEffect(() => {
    refreshModels();
  }, [refreshModels]);

  useEffect(() => {
    if (!customerId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await listFinancialSnapshotVersionsApi(customerId);
        if (cancelled) {
          return;
        }
        setSnapshotVersions(rows || []);
        if (rows?.length) {
          const latestId = rows[0].id;
          setSelectedSnapshotVersionId(latestId);
          const version = await getFinancialSnapshotVersionApi(latestId);
          if (cancelled || !version?.snapshot) {
            return;
          }
          setState((s) => hydrateModelFromFinancialSnapshot(s, version.snapshot));
          setSnapshotHydrated(true);
          return;
        }
        const profile = await getCustomerCockpitProfileApi(customerId);
        if (cancelled || !profile?.financialSnapshot) {
          return;
        }
        setState((s) => hydrateModelFromFinancialSnapshot(s, profile.financialSnapshot));
        setSnapshotHydrated(true);
      } catch {
        try {
          const profile = await getCustomerCockpitProfileApi(customerId);
          if (cancelled || !profile?.financialSnapshot) {
            return;
          }
          setState((s) => hydrateModelFromFinancialSnapshot(s, profile.financialSnapshot));
          setSnapshotHydrated(true);
        } catch {
          // Create still works with saved models when snapshot is missing
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const onSnapshotVersionChange = useCallback(async (versionId) => {
    setSelectedSnapshotVersionId(versionId);
    if (!versionId) {
      return;
    }
    try {
      const version = await getFinancialSnapshotVersionApi(versionId);
      if (!version?.snapshot) {
        return;
      }
      setState((s) => hydrateModelFromFinancialSnapshot(s, version.snapshot));
      setSnapshotHydrated(true);
    } catch {
      toast({
        title: 'Could not load snapshot version',
        status: 'warning',
      });
    }
  }, [toast]);

  const paybackMonth = useMemo(() => {
    const idx = state.roas.findIndex((v) => v >= 100);
    return idx >= 0 ? `M${idx}` : '—';
  }, [state.roas]);

  const finalRoas = state.roas[state.roas.length - 1] || 0;
  const facilityFeeAmount = resolveFacilityFeeAmount(state);
  const facilityFeeInstallmentCount = Math.max(
    1,
    Math.min(
      MAX_FACILITY_FEE_INSTALLMENTS,
      Math.floor(Number(state.facilityFeeInstallments ?? 1))
    )
  );
  const facilityFeePerInstallment =
    facilityFeeAmount > 0
      ? Math.floor(facilityFeeAmount / facilityFeeInstallmentCount)
      : 0;

  const firstMarketingMonth = resolveFirstMarketingMonth(state);
  const planStartYear = firstMarketingMonth.getFullYear();
  const planStartMonth = firstMarketingMonth.getMonth();

  const calendarIndexedRows = isResultsCalendarAligned(state);

  const planMonths = useMemo(
    () =>
      marketingPlanMonthLabels(
        state.marketing.length,
        planStartYear,
        planStartMonth,
        calendarIndexedRows
      ),
    [
      state.marketing.length,
      planStartYear,
      planStartMonth,
      calendarIndexedRows,
    ]
  );

  const planMarketingMonthCount = state.marketing.length;

  const totalMarketingSpend = useMemo(
    () => state.marketing.reduce((sum, value) => sum + value, 0),
    [state.marketing]
  );

  const hillModel = useMemo(
    () =>
      hasCohortSheet(state.cohortSheet)
        ? fitHillRoasModel(state.cohortSheet)
        : null,
    [state.cohortSheet]
  );
  const useHillRoas = resolveUseHillRoasForFunding(state, hillModel);
  const effectiveRoasLength = useMemo(
    () => (useHillRoas ? hillModel.maxRoasByMob.length : state.roas.length),
    [useHillRoas, hillModel, state.roas.length]
  );

  const spendRoasVectors = useMemo(() => {
    if (!useHillRoas || !hillModel) {
      return null;
    }
    return buildHillSpendRoasVectors(hillModel, state.marketing, state);
  }, [
    useHillRoas,
    hillModel,
    state.marketing,
    state.hillRoasEfficiencyRatio,
  ]);

  useEffect(() => {
    setState((s) => {
      if (isManualUwSource(s.runwaySource)) {
        return s;
      }
      const fromBurn = calcRunwayFromNetBurn(s.beginCash, s.netBurn);
      if (fromBurn != null) {
        if (s.runway === fromBurn && s.runwaySource === 'Calculated') {
          return s;
        }
        return {
          ...s,
          runway: fromBurn,
          runwaySource: 'Calculated',
        };
      }
      const fromSnapshot = roundRunwayMonths(s.accountingSnapshot?.runway?.value);
      if (fromSnapshot != null) {
        if (s.runway === fromSnapshot && s.runwaySource === 'From accounting') {
          return s;
        }
        return {
          ...s,
          runway: fromSnapshot,
          runwaySource: 'From accounting',
        };
      }
      if (s.runway == null && s.runwaySource === 'Default') {
        return s;
      }
      return {
        ...s,
        runway: null,
        runwaySource: 'Default',
      };
    });
  }, [state.beginCash, state.netBurn, state.accountingSnapshot]);

  useEffect(() => {
    if (!hasCohortSheet(state.cohortSheet)) {
      return;
    }
    setState((s) => {
      let next = s;
      const cohortLatest = resolveLatestCohortMarketingSpend(s.cohortSheet) ?? 0;
      if (
        shouldAutoSyncMarketingKpiField(
          s.lastMarketingSpendSource,
          s.lastMarketingSpend,
          cohortLatest
        )
      ) {
        if (next.lastMarketingSpend !== cohortLatest) {
          next = {
            ...next,
            lastMarketingSpend: cohortLatest,
            lastMarketingSpendSource: cohortLatest
              ? 'From cohort data'
              : next.lastMarketingSpendSource,
          };
        }
      }
      const hasPredicted = s.cohortSheet.cohorts.some((row) =>
        row.periods?.some((period) => period.isPredicted)
      );
      const cohortPredictedRunoff = hasPredicted
        ? getCohortSummary(s.cohortSheet).runoff12Months
        : 0;
      if (
        shouldAutoSyncMarketingKpiField(
          s.predictedRunoffSource,
          s.predictedRunoff,
          cohortPredictedRunoff
        )
      ) {
        if (next.predictedRunoff !== cohortPredictedRunoff) {
          next = {
            ...next,
            predictedRunoff: cohortPredictedRunoff,
            predictedRunoffSource: hasPredicted
              ? 'From cohort data'
              : next.predictedRunoffSource,
          };
        }
      }
      const referenceSpend =
        next.lastMarketingSpend > 0
          ? next.lastMarketingSpend
          : resolveLatestCohortMarketingSpend(next.cohortSheet);
      const model = fitHillRoasModel(next.cohortSheet);
      const targetMobIndex = resolveMaxRoas100TargetMob(model);
      const maxSpend =
        model && referenceSpend && targetMobIndex != null
          ? maxSpendForTargetRoasHill(
              model,
              targetMobIndex,
              UA_PREDICTION_TARGET_ROAS
            )
          : null;
      const resolvedMax = maxSpend ?? 0;
      if (
        shouldAutoSyncMarketingKpiField(
          s.maxMarketingRoas100Source,
          s.maxMarketingRoas100,
          resolvedMax
        )
      ) {
        if (next.maxMarketingRoas100 !== resolvedMax) {
          next = {
            ...next,
            maxMarketingRoas100: resolvedMax,
            maxMarketingRoas100Source: resolvedMax > 0
              ? 'Hill model'
              : next.maxMarketingRoas100Source,
          };
        }
      }
      const { ratio: rawCacRatio } = resolveUaPredictionInputs({
        cohortSheet: next.cohortSheet,
        hillModel: model,
        lastMarketingSpend: next.lastMarketingSpend,
        maxMarketingRoas100: next.maxMarketingRoas100,
      });
      const cacRatio =
        rawCacRatio != null && Number.isFinite(Number(rawCacRatio))
          ? parsePercentInput(String(rawCacRatio), 2)
          : null;
      if (
        shouldAutoSyncMarketingKpiField(
          s.cacPredictionRatioSource,
          s.cacPredictionRatio,
          cacRatio
        )
      ) {
        if (next.cacPredictionRatio !== cacRatio) {
          next = {
            ...next,
            cacPredictionRatio: cacRatio,
            cacPredictionRatioSource: cacRatio != null ? 'Calculated' : next.cacPredictionRatioSource,
          };
        }
      }
      return next === s ? s : next;
    });
  }, [state.cohortSheet]);

  useEffect(() => {
    setState((s) => {
      if (isManualUwSource(s.liquidValueSource)) {
        return s;
      }
      const predictedRunoffFromManual = isManualUwSource(s.predictedRunoffSource)
        ? s.predictedRunoff
        : null;
      const cohortRunoff = hasCohortSheet(s.cohortSheet)
        ? getCohortSummary(s.cohortSheet).runoff12Months
        : 0;
      const runoff12 = predictedRunoffFromManual ?? s.predictedRunoff ?? cohortRunoff;
      const liquid = calcLiquidValueOpexMultiple(
        s.beginCash,
        runoff12,
        s.debt,
        s.opex
      );
      if (liquid == null) {
        return s;
      }
      if (
        shouldAutoSyncMarketingKpiField(
          s.liquidValueSource,
          s.liquidValue,
          liquid
        )
      ) {
        if (s.liquidValue === liquid && s.liquidValueSource === 'Calculated') {
          return s;
        }
        return {
          ...s,
          liquidValue: liquid,
          liquidValueSource: 'Calculated',
        };
      }
      return s;
    });
  }, [
    state.beginCash,
    state.debt,
    state.opex,
    state.predictedRunoff,
    state.predictedRunoffSource,
    state.cohortSheet,
  ]);

  useEffect(() => {
    setState((s) => {
      if (isManualUwSource(s.cashBalanceSource)) {
        return s;
      }
      const computed = s.opex > 0 ? calcCashOpexMultiple(s.beginCash, s.opex) : null;
      if (computed != null) {
        if (s.cashBalance === computed && s.cashBalanceSource === 'Calculated') {
          return s;
        }
        return {
          ...s,
          cashBalance: computed,
          cashBalanceSource: 'Calculated',
        };
      }
      const fromSnapshot = s.accountingSnapshot?.cashBalance?.value;
      if (fromSnapshot != null && Number.isFinite(Number(fromSnapshot))) {
        const snapshotValue = Number(fromSnapshot);
        if (s.cashBalance === snapshotValue && s.cashBalanceSource === 'From accounting') {
          return s;
        }
        return {
          ...s,
          cashBalance: snapshotValue,
          cashBalanceSource: 'From accounting',
        };
      }
      if (s.cashBalance == null && s.cashBalanceSource === 'Default') {
        return s;
      }
      return {
        ...s,
        cashBalance: null,
        cashBalanceSource: 'Default',
      };
    });
  }, [state.beginCash, state.opex, state.accountingSnapshot]);

  useEffect(() => {
    setState((s) => {
      if (isManualUwSource(s.balanceSheetSource)) {
        return s;
      }
      const computed = calcBalanceSheetRatio(
        s.totalAssets,
        s.totalLiabilities,
        s.safe
      );
      if (computed != null) {
        if (s.balanceSheet === computed && s.balanceSheetSource === 'Calculated') {
          return s;
        }
        return {
          ...s,
          balanceSheet: computed,
          balanceSheetSource: 'Calculated',
        };
      }
      const fromSnapshot = s.accountingSnapshot?.balanceSheet?.value;
      if (fromSnapshot != null && Number.isFinite(Number(fromSnapshot))) {
        const snapshotValue = Number(fromSnapshot);
        if (s.balanceSheet === snapshotValue && s.balanceSheetSource === 'From accounting') {
          return s;
        }
        return {
          ...s,
          balanceSheet: snapshotValue,
          balanceSheetSource: 'From accounting',
        };
      }
      if (s.balanceSheet == null && s.balanceSheetSource === 'Default') {
        return s;
      }
      return {
        ...s,
        balanceSheet: null,
        balanceSheetSource: 'Default',
      };
    });
  }, [
    state.totalAssets,
    state.totalLiabilities,
    state.safe,
    state.accountingSnapshot,
  ]);

  const uwScorecardTotals = useMemo(() => {
    if (!scorecardConfig?.metrics?.length || !hasCohortSheet(state.cohortSheet)) {
      return null;
    }
    const scoringRows = buildUwScoringRows({
      cohortSheet: state.cohortSheet,
      monthlyOpex: state.opex,
      beginCash: state.beginCash,
      debt: state.debt ?? 0,
      totalAssets: state.totalAssets ?? 0,
      totalAssetsSource: state.totalAssetsSource ?? 'Default',
      totalLiabilities: state.totalLiabilities ?? 0,
      totalLiabilitiesSource: state.totalLiabilitiesSource ?? 'Default',
      safe: state.safe ?? 0,
      netBurn: state.netBurn ?? 0,
      runway: state.runway ?? null,
      runwaySource: state.runwaySource ?? 'Default',
      predictedRunoff: state.predictedRunoff ?? null,
      predictedRunoffSource: state.predictedRunoffSource ?? 'Default',
      liquidValue: state.liquidValue ?? null,
      liquidValueSource: state.liquidValueSource ?? 'Default',
      cashBalance: state.cashBalance ?? null,
      cashBalanceSource: state.cashBalanceSource ?? 'Default',
      balanceSheet: state.balanceSheet ?? null,
      balanceSheetSource: state.balanceSheetSource ?? 'Default',
      cacPredictionRatio: state.cacPredictionRatio ?? null,
      cacPredictionRatioSource: state.cacPredictionRatioSource ?? 'Default',
      revenueGrowthYoy: state.revenueGrowthYoy ?? null,
      revenueGrowthYoySource: state.revenueGrowthYoySource ?? 'Default',
      lastMarketingSpend: state.lastMarketingSpend ?? 0,
      maxMarketingRoas100: state.maxMarketingRoas100 ?? 0,
      hillModel,
      scorecardConfig,
      accountingSnapshot: state.accountingSnapshot ?? null,
      investorScore: state.investorScore,
      managementScore: state.managementScore,
      stressTestingScore: state.stressTestingScore,
    });
    return computeUwScorecardTotals(scoringRows);
  }, [state, hillModel, scorecardConfig]);

  const exposureGradeLimits = useMemo(() => {
    if (!uwScorecardTotals?.overallGrade) {
      return null;
    }
    return resolveExposureLimitsForOverallGrade(
      uwScorecardTotals.overallGrade,
      scorecardConfig?.gradeThresholds
    );
  }, [uwScorecardTotals, scorecardConfig]);

  const excelDownloadEnabled = useMemo(
    () =>
      Boolean(calcResult) || hasCohortSheet(state.cohortSheet),
    [calcResult, state.cohortSheet]
  );

  const handleSaveToDrive = useCallback(async () => {
    if (!customerId) {
      toast({
        title: 'Save to Drive failed',
        description: 'Customer context is required.',
        status: 'error',
      });
      return;
    }
    setSavingToDrive(true);
    try {
      const resolvedScorecardConfig =
        scorecardConfig ?? (await getLatestScorecardConfig().catch(() => null));
      const exportOptions =
        buildCohortExportOptionsFromState(state, {
          scorecardConfig: resolvedScorecardConfig,
          hillModel,
          companyName: customer?.companyName,
        }) ?? {
          companyName: customer?.companyName,
          scorecardConfig: resolvedScorecardConfig,
        };
      const result = await exportQuickCashflowToDrive({
        customerId,
        calcResult,
        state,
        calcTimestamp,
        filenameBase: state.modelName?.trim() || 'quick-cashflow',
        cohortExportOptions: exportOptions,
        scenarioResults,
        hillModel,
        includeCashflowSheets: true,
      });
      toast(quickCashflowDriveExportSuccessToast(result));
    } catch (err) {
      toast({
        title: 'Save to Drive failed',
        description:
          err?.response?.data?.error ||
          err?.message ||
          'Could not upload the export to Google Drive.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSavingToDrive(false);
    }
  }, [
    calcResult,
    scenarioResults,
    calcTimestamp,
    state,
    hillModel,
    scorecardConfig,
    customer?.companyName,
    customerId,
    toast,
  ]);

  const downloadFilenameBase = state.modelName?.trim() || 'quick-cashflow';

  const handleDownloadCohortExcel = useCallback(async () => {
    try {
      const exportOptions = buildCohortExportOptionsFromState(state, {
        scorecardConfig,
        hillModel,
        companyName: customer?.companyName,
      });
      await downloadCohortSheetExcel(
        state.cohortSheet,
        downloadFilenameBase,
        exportOptions ?? {}
      );
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err?.message || 'Could not download the cohort workbook.',
        status: 'error',
      });
    }
  }, [
    state,
    scorecardConfig,
    hillModel,
    customer?.companyName,
    downloadFilenameBase,
    toast,
  ]);

  const handleDownloadPlExcel = useCallback(async () => {
    try {
      await downloadQuickCashflowExcel({
        calcResult,
        state,
        calcTimestamp,
        filenameBase: downloadFilenameBase,
        scenarioResults,
        cohortExportOptions: customer?.companyName
          ? { companyName: customer.companyName }
          : null,
      });
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err?.message || 'Could not download the P&L workbook.',
        status: 'error',
      });
    }
  }, [
    calcResult,
    state,
    calcTimestamp,
    downloadFilenameBase,
    scenarioResults,
    customer?.companyName,
    toast,
  ]);

  useEffect(() => {
    return () => {
      if (extractPollRef.current) {
        clearInterval(extractPollRef.current);
      }
    };
  }, []);

  const pollExtractJob = useCallback(
    (modelId, jobId) => {
      if (extractPollRef.current) {
        clearInterval(extractPollRef.current);
      }
      extractPollRef.current = setInterval(async () => {
        try {
          const status = await getQuickCashflowExtractStatus(modelId || 'draft', jobId);
          const { extractJob, qcf } = resolveExtractPollPayload(status);
          if (extractJob?.status === 'complete' && qcf) {
            clearInterval(extractPollRef.current);
            extractPollRef.current = null;
            const usageText = formatAssistTokenUsage(qcf.usage);
            if (usageText) {
              setExtractUsage(usageText);
              setExtractChatAppend({
                id: `extract-${jobId}`,
                text: 'Accounting extraction finished.',
                usage: usageText,
              });
            } else {
              setExtractUsage(null);
            }
            setState((s) =>
              applyExtractPollTerminal(applyAssistResponse(s, qcf, []), qcf, extractJob)
            );
          } else if (extractJob?.status === 'cancelled') {
            clearInterval(extractPollRef.current);
            extractPollRef.current = null;
            setExtractUsage(null);
            if (qcf) {
              setState((s) =>
                applyExtractPollTerminal(applyAssistResponse(s, qcf, []), qcf, extractJob)
              );
            }
          } else if (extractJob?.status === 'failed') {
            clearInterval(extractPollRef.current);
            extractPollRef.current = null;
            if (qcf) {
              setState((s) => applyExtractPollTerminal(applyAssistResponse(s, qcf, []), qcf, extractJob));
            } else if (status?.assistSession) {
              setState((s) => ({
                ...s,
                assistSession: status.assistSession,
              }));
            }
            toast({
              title: 'Extraction failed',
              description: extractJob.error || qcf?.explanation || 'Unknown error',
              status: 'error',
            });
          } else if (extractJob?.status === 'running' || extractJob?.status === 'pending') {
            setState((s) => ({
              ...s,
              assistSession: {
                ...(qcf?.assistSession || s.assistSession || {}),
                extractJob: extractJob || qcf?.assistSession?.extractJob || s.assistSession?.extractJob,
              },
              accountingSnapshot:
                qcf?.accountingSnapshot || s.accountingSnapshot,
            }));
          } else if (qcf?.assistSession) {
            setState((s) => ({
              ...s,
              assistSession: qcf.assistSession,
              accountingSnapshot:
                qcf.accountingSnapshot || s.accountingSnapshot,
            }));
          }
        } catch {
          // keep polling until timeout or success
        }
      }, 3000);
    },
    [toast]
  );

  const handleAssistSend = useCallback(
    async ({ message, files, history, cohortColumnMapping, signal }) => {
      try {
        const response = await quickCashflowAssistApi({
          message,
          files,
          history,
          cohortColumnMapping,
          assistSession: {
            ...(assistSessionRef.current ?? createDefaultAssistSession()),
            customerId,
          },
          cohortSheet: cohortSheetRef.current,
          customerId,
          signal,
        });
        setState((s) =>
          applyAssistResponse(
            s,
            response,
            files.map((f) => f.name)
          )
        );
        if (response?.assistSession) {
          assistSessionRef.current = response.assistSession;
        }
        if (
          response?.extractJob?.jobId &&
          response.extractJob.status === 'running'
        ) {
          setExtractUsage(null);
          setExtractChatAppend(null);
          pollExtractJob(loadId, response.extractJob.jobId);
        }
        return {
          text: buildAssistChatText(response),
          usage: response.usage,
          awaitingMappingConfirmation:
            response?.cohort?.clarificationKind === 'mapping_confirmation',
          cohortColumnMapping: response.cohort?.columnMapping,
        };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
          throw err;
        }
        const msg = err?.response?.data?.error || err.message;
        toast({ title: 'AI assist failed', description: msg, status: 'error' });
        throw err;
      }
    },
    [toast, loadId, pollExtractJob, customerId]
  );

  const performSave = async () => {
    const name = state.modelName.trim();
    if (!name) {
      toast({ title: 'Enter a model name first', status: 'warning' });
      return;
    }
    if (!customerId) {
      toast({ title: 'Customer context is required', status: 'error' });
      return;
    }
    try {
      const saved = await saveQuickCashflowModel({
        customerId,
        name,
        model: state,
        result:
          calcResult && calcTimestamp
            ? { calcResult, calcTimestamp: calcTimestamp.toISOString() }
            : undefined,
      });
      if (saved?.id) {
        setLoadId(saved.id);
      }
      toast({ title: `Saved "${name}"`, status: 'success' });
      await refreshModels();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err?.response?.data?.error || err.message,
        status: 'error',
      });
    }
  };

  const onSave = () => {
    const name = state.modelName.trim();
    if (!name) {
      toast({ title: 'Enter a model name first', status: 'warning' });
      return;
    }
    if (!customerId) {
      toast({ title: 'Customer context is required', status: 'error' });
      return;
    }

    const overwriteTarget = resolveSaveOverwriteTarget({
      loadId,
      modelName: name,
      savedModels,
    });
    if (overwriteTarget) {
      setSaveOverwriteTarget(overwriteTarget);
      openSaveConfirm();
      return;
    }

    performSave();
  };

  const onConfirmSave = async () => {
    closeSaveConfirm();
    await performSave();
  };

  const handleCancelExtract = useCallback(async () => {
    const jobId = assistSessionRef.current?.extractJob?.jobId;
    if (!jobId) {
      return;
    }
    setCancellingExtract(true);
    try {
      const status = await cancelQuickCashflowExtractJob(jobId);
      if (extractPollRef.current) {
        clearInterval(extractPollRef.current);
        extractPollRef.current = null;
      }
      const { extractJob, qcf } = resolveExtractPollPayload(status);
      setExtractUsage(null);
      if (qcf) {
        setState((s) =>
          applyExtractPollTerminal(applyAssistResponse(s, qcf, []), qcf, extractJob)
        );
        if (qcf.assistSession) {
          assistSessionRef.current = qcf.assistSession;
        }
      }
    } catch (err) {
      toast({
        title: 'Could not cancel extraction',
        description: err?.response?.data?.error || err.message,
        status: 'error',
      });
    } finally {
      setCancellingExtract(false);
    }
  }, [toast]);

  const handleDeleteOpenAIFiles = useCallback(async () => {
    if (!loadId) {
      toast({
        title: 'Save the model first',
        description: 'Save this model before deleting OpenAI files.',
        status: 'warning',
      });
      return;
    }
    setDeletingOpenAIFiles(true);
    try {
      const result = await deleteQuickCashflowModelOpenAIFiles(loadId);
      setState((s) => ({
        ...s,
        assistSession: {
          ...(s.assistSession || {}),
          activeOpenAIFileIds: [],
          openaiFilesDeletedAt: new Date().toISOString(),
        },
      }));
      toast({
        title: result.message || 'OpenAI files deleted',
        status: result.deleted ? 'success' : 'info',
      });
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err?.response?.data?.error || err.message,
        status: 'error',
      });
    } finally {
      setDeletingOpenAIFiles(false);
    }
  }, [loadId, toast]);

  const onLoad = async (id) => {
    if (!id) return;
    try {
      const doc = await getQuickCashflowModel(id);
      const model = typeof doc.model === 'string' ? JSON.parse(doc.model) : doc.model;
      setState({
        ...createEmptyModel(),
        ...applyCohortAnchoredPlanStart(
          ensureMarketingPlanIncludesCurrentMonth(model)
        ),
        modelName: doc.name,
      });
      setLoadId(id);
      if (doc.result) {
        const parsed =
          typeof doc.result === 'string' ? JSON.parse(doc.result) : doc.result;
        if (parsed.calcResult) {
          setCalcResult(hydrateCalcResult(parsed.calcResult));
          setScenarioResults(null);
          setCalcTimestamp(
            parsed.calcTimestamp ? new Date(parsed.calcTimestamp) : null
          );
          setResultsVisible(true);
        }
      } else {
        setCalcResult(null);
        setScenarioResults(null);
        setResultsVisible(false);
      }
      toast({ title: `Loaded "${doc.name}"`, status: 'success' });
    } catch (err) {
      toast({
        title: 'Load failed',
        description: err?.response?.data?.error || err.message,
        status: 'error',
      });
    }
  };

  const onDeleteModel = async () => {
    if (!loadId) {
      toast({
        title: 'Save the model first',
        description: 'Load or save a model before deleting.',
        status: 'warning',
      });
      return;
    }
    const loaded = savedModels.find((m) => m.id === loadId);
    const name = loaded?.name || state.modelName || 'this model';
    if (
      !window.confirm(
        `Delete "${name}"? This removes the saved model and any OpenAI files attached to it.`
      )
    ) {
      return;
    }
    setDeletingModel(true);
    try {
      await deleteQuickCashflowModel(loadId);
      setLoadId('');
      setState(createEmptyModel());
      setCalcResult(null);
      setScenarioResults(null);
      setCalcTimestamp(null);
      setResultsVisible(false);
      setAssistChatKey((k) => k + 1);
      setCalcStatus('Ready — edit inputs and press Calculate.');
      await refreshModels();
      toast({ title: `Deleted "${name}"`, status: 'success' });
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err?.response?.data?.error || err.message,
        status: 'error',
      });
    } finally {
      setDeletingModel(false);
    }
  };

  const onCalculate = () => {
    setCalculating(true);
    setCalcStatus('Calculating…');
    setTimeout(() => {
      const { results, autoFix } = runScenarios(
        state,
        exposureGradeLimits,
        planMonths,
        state.scenarios
      );
      const result = results.base;
      setCalcResult(result);
      setScenarioResults({ bull: results.bull, bear: results.bear });
      setCalcTimestamp(new Date());
      setResultsVisible(true);
      const autoFixToast = buildAutoFixFeedback(autoFix);
      if (autoFixToast) {
        toast(autoFixToast);
      }
      const hasDraws = stateHasDraws(state);
      const allRevShareZero = state.marketingPlanManualMode
        ? state.marketing.some(
            (_, index) =>
              resolveCohortDraw(state, index) > 0 &&
              Number(state.revSharePct[index]) === 0
          )
        : hasDraws && resolveDefaultRevSharePct(state) === 0;
      if (result.totalRepayments === 0 && hasDraws && allRevShareZero) {
        setCalcStatus(
          'Repayments are zero — set % revenue share in the marketing plan (currently 0%).'
        );
      } else {
        setCalcStatus('Calculation complete. Edit any input and Calculate again.');
      }
      setCalculating(false);
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
  };

  const onReset = () => {
    if (!window.confirm('Clear all inputs and start fresh?')) return;
    setState(createEmptyModel());
    setCalcResult(null);
    setScenarioResults(null);
    setResultsVisible(false);
    setAssistChatKey((k) => k + 1);
    setLoadId('');
    setCalcStatus('Ready — edit inputs and press Calculate.');
    toast({ title: 'Cleared', status: 'info', duration: 2000 });
  };

  const updateRoas = (index, value) => {
    setState((s) => {
      const roas = [...s.roas];
      roas[index] = Number(value) || 0;
      return { ...s, roas };
    });
  };

  const updateScenario = (key, next) => {
    setState((s) => ({
      ...s,
      scenarios: { ...(s.scenarios ?? {}), [key]: next },
    }));
  };

  const addRoasPeriod = () => {
    setState((s) => {
      const n = s.roas.length;
      const last = s.roas[n - 1] || 0;
      const prev = s.roas[n - 2] || 0;
      const marginal = Math.max(0, last - prev);
      return { ...s, roas: [...s.roas, Number((last + marginal).toFixed(1))] };
    });
  };

  const removeRoasPeriod = () => {
    setState((s) => {
      if (s.roas.length <= 1) return s;
      return { ...s, roas: s.roas.slice(0, -1) };
    });
  };

  const addMarketingMonth = () => {
    setState((s) => {
      if (s.marketing.length >= MAX_PLAN_MARKETING_MONTHS) {
        return s;
      }
      return padPlanArrays(s, s.marketing.length + 1);
    });
  };

  const removeMarketingMonth = () => {
    setState((s) => {
      if (s.marketing.length <= 1) {
        return s;
      }
      return {
        ...s,
        marketing: s.marketing.slice(0, -1),
        fundingPct: s.fundingPct.slice(0, -1),
        revSharePct: s.revSharePct.slice(0, -1),
        planMaxExposure: (s.planMaxExposure ?? []).slice(0, -1),
        planMaxMonthlyDraw: (s.planMaxMonthlyDraw ?? []).slice(0, -1),
        planDrawAmount: (s.planDrawAmount ?? []).slice(0, -1),
      };
    });
  };

  const pasteRoas = () => {
    const v = window.prompt(
      'Paste cumulative ROAS values (comma- or space-separated):'
    );
    if (!v) return;
    const nums = v
      .split(/[,\s]+/)
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    if (!nums.length) {
      toast({ title: 'Need at least 1 value', status: 'warning' });
      return;
    }
    setState((s) => ({
      ...s,
      roas: nums,
      roasSource: 'Pasted',
      roasMode: 'manual',
    }));
  };

  const pasteMarketingSpend = () => {
    const v = window.prompt(
      'Paste marketing spend values (comma-, space-, or tab-separated):'
    );
    if (!v) return;
    const nums = parseMarketingSpendPaste(v);
    if (!nums.length) {
      toast({ title: 'Need at least 1 value', status: 'warning' });
      return;
    }
    setState((s) => applyPastedMarketingSpend(s, nums, 0));
  };

  return (
    <Box className="quick-cashflow-page">
      <Box
        className="card"
        position="sticky"
        top={0}
        zIndex={10}
        px={8}
        py={5}
        mb={8}
      >
        <Flex align="flex-start" gap={4} flexWrap="wrap">
          <Box>
            <Text fontSize="xs" textTransform="uppercase" color="gray.400">
              First Underwriting — Cashflow
            </Text>
            <Text fontSize="2xl" fontWeight="bold" className="brand">
              {customer?.companyName || 'Quick Cash Flow Model'}
            </Text>
          </Box>
          <Box flex={1} />
          <Box minW={{ base: '100%', md: '360px' }} maxW="560px">
            <Flex align="center" gap={2} mb={3} flexWrap="wrap" justify="flex-end">
              <Text
                fontSize="sm"
                color="gray.500"
                fontWeight="medium"
                whiteSpace="nowrap"
              >
                Data Extractor snapshot
              </Text>
              <Select
                flex="1"
                minW="220px"
                size="sm"
                placeholder="Latest snapshot…"
                value={selectedSnapshotVersionId}
                onChange={(e) => onSnapshotVersionChange(e.target.value)}
              >
                {snapshotVersions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {snapshotVersionLabel(version)}
                  </option>
                ))}
              </Select>
            </Flex>
            <Flex align="center" gap={2} flexWrap="wrap" justify="flex-end">
              <Select
                w="180px"
                size="sm"
                placeholder="Load model…"
                value={loadId}
                onChange={(e) => onLoad(e.target.value)}
              >
                {savedModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {savedModelLabel(m)}
                  </option>
                ))}
              </Select>
              <Input
                w="160px"
                size="sm"
                value={state.modelName}
                onChange={(e) =>
                  setState((s) => ({ ...s, modelName: e.target.value }))
                }
                placeholder="Model name"
              />
              <Flex align="center" gap={2} flexShrink={0}>
                <Button variant="outline" colorScheme="purple" size="sm" onClick={onSave}>
                  Save
                </Button>
                <Button
                  variant="outline"
                  colorScheme="red"
                  size="sm"
                  onClick={onDeleteModel}
                  isLoading={deletingModel}
                  isDisabled={!loadId}
                >
                  Delete
                </Button>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </Box>

      <Box className="section-stack" px={8} maxW="1400px" mx="auto" pt={2}>
        {/* Step 1 — ingest lives on Data Extractor; Create scores from canonical snapshot */}
        <Box className="card">
          <Flex className="section-header" align="center" gap={3} flexWrap="wrap">
            <span className="step-num">1</span>
            <Box flex={1}>
              <Text fontWeight="semibold">Financial data (from Data Extractor)</Text>
              <Text className="annotation">
                Upload, Drive extract, and cohort processing run on{' '}
                <Link to={`/customers/${customerId}/first-underwriting/data`}>
                  First Underwriting → Data Extractor
                </Link>
                . Create loads the latest Data Extractor snapshot for scoring by default; pick
                another version above to score from historical extract data. Edits here apply to
                this model only — change canonical values on Data Extractor.
                {snapshotHydrated ? ' Snapshot loaded.' : ''}
              </Text>
            </Box>
          </Flex>
        </Box>

        {/* Step 2 (was cohort — renumbered in UI as step 2) */}
        <Box className="card">
          <Flex
            className={`section-header${cohortAnalysisExpanded ? '' : ' section-header-collapsed'}`}
            justify="space-between"
            gap={3}
            align="center"
            flexWrap="wrap"
          >
            <Flex gap={3} align="center" flex={1} minW={0}>
              <span className="step-num">2</span>
              <Box flex={1} minW={0}>
                <button
                  type="button"
                  className="section-toggle"
                  onClick={() => setCohortAnalysisExpanded((v) => !v)}
                >
                  {cohortAnalysisExpanded ? '▼' : '▶'} Cohort Analysis
                </button>
                {cohortAnalysisExpanded ? (
                  <Text className="annotation">
                    Cumulative ROAS defines how each marketing dollar returns
                    revenue over time.
                  </Text>
                ) : (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Cumulative ROAS {finalRoas.toFixed(0)}% · Payback month{' '}
                    {paybackMonth}
                  </Text>
                )}
              </Box>
            </Flex>
            <DownloadExcelButton
              onDownload={handleDownloadCohortExcel}
              isDisabled={!state.cohortSheet?.cohorts?.length}
            />
          </Flex>
          {cohortAnalysisExpanded ? (
          <Box p={5}>
            <Box
              border="1px solid"
              borderColor="purple.100"
              borderRadius="lg"
              p={4}
              bg="purple.50"
            >
              <Flex justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                <Flex align="center" gap={2} flexWrap="wrap">
                  <Text fontWeight="semibold">Cumulative ROAS vector</Text>
                  <HelperTooltip content={ROAS_VECTOR_TOOLTIP} />
                  <SourceBadge label={state.roasSource} />
                  {useHillRoas ? (
                    <span className="source-badge">
                      Not used — Hill vectors from cohort data
                    </span>
                  ) : null}
                </Flex>
                {!useHillRoas ? (
                  <Flex gap={1}>
                    <Button
                      size="xs"
                      variant={state.roasMode === 'ai' ? 'solid' : 'ghost'}
                      colorScheme="purple"
                      onClick={() =>
                        setState((s) => ({ ...s, roasMode: 'ai' }))
                      }
                    >
                      AI suggested
                    </Button>
                    <Button
                      size="xs"
                      variant={state.roasMode === 'manual' ? 'solid' : 'ghost'}
                      colorScheme="purple"
                      onClick={() =>
                        setState((s) => ({
                          ...s,
                          roasMode: 'manual',
                          roasSource: 'Manual entry',
                        }))
                      }
                    >
                      Manual entry
                    </Button>
                  </Flex>
                ) : null}
              </Flex>
              <Flex gap={2} mb={3} flexWrap="wrap" align="center">
                {!useHillRoas ? (
                  <>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() =>
                        setState((s) => ({
                          ...s,
                          roas: [...DEFAULT_ROAS],
                          roasSource: 'Default preset',
                          roasMode: 'ai',
                        }))
                      }
                    >
                      Reset to AI
                    </Button>
                    <Button size="xs" variant="ghost" onClick={pasteRoas}>
                      Paste values
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() =>
                        setState((s) => ({
                          ...s,
                          roas: Array(s.roas.length).fill(0),
                          roasSource: 'Manual entry',
                          roasMode: 'manual',
                        }))
                      }
                    >
                      Clear all
                    </Button>
                  </>
                ) : null}
                <Box ml="auto" display="flex" alignItems="center" gap={2}>
                  <CopyRoasVectorButton vectorPct={state.roas} />
                  <Text className="annotation">
                    Vector length: <b>{state.roas.length}</b> months
                  </Text>
                  {!useHillRoas ? (
                    <>
                      <Button size="xs" onClick={removeRoasPeriod}>
                        −
                      </Button>
                      <Button size="xs" onClick={addRoasPeriod}>
                        +
                      </Button>
                    </>
                  ) : null}
                </Box>
              </Flex>
              <Flex
                flexWrap="wrap"
                gap={2}
                mb={3}
                bg="white"
                p={3}
                borderRadius="lg"
                opacity={useHillRoas ? 0.55 : 1}
              >
                {state.roas.map((v, i) => (
                  <Box key={i} className="roas-cell" textAlign="center">
                    <Text className="annotation" mb={1}>
                      M{i}
                    </Text>
                    <Input
                      type="number"
                      size="sm"
                      textAlign="center"
                      value={v}
                      isReadOnly={useHillRoas}
                      onChange={(e) => updateRoas(i, e.target.value)}
                    />
                  </Box>
                ))}
              </Flex>
              <Text fontSize="sm" className="annotation">
                Final cumulative ROAS:{' '}
                <span className="accent-text" style={{ fontWeight: 600 }}>
                  {finalRoas.toFixed(0)}%
                </span>{' '}
                · Payback month:{' '}
                <span style={{ fontWeight: 600 }}>{paybackMonth}</span>
              </Text>
            </Box>
            {hillModel ? (
              <Box
                mt={4}
                border="1px solid"
                borderColor="purple.200"
                borderRadius="lg"
                p={4}
                bg="white"
              >
                <HillMaxRoasSection hillModel={hillModel} />
              </Box>
            ) : null}
            {hillModel ? (
              <FutureFundingRoasSection
                source={
                  state.futureFundingRoasSource ===
                  FUTURE_FUNDING_ROAS_SOURCE.GENERAL
                    ? FUTURE_FUNDING_ROAS_SOURCE.GENERAL
                    : FUTURE_FUNDING_ROAS_SOURCE.HILL
                }
                onChangeSource={(futureFundingRoasSource) =>
                  setState((s) => ({ ...s, futureFundingRoasSource }))
                }
                learningFactor={state.hillRoasEfficiencyRatio ?? 1}
                onChangeLearningFactor={(hillRoasEfficiencyRatio) =>
                  setState((s) => ({ ...s, hillRoasEfficiencyRatio }))
                }
              />
            ) : null}
            {hasCohortSheet(state.cohortSheet) && !hillModel ? (
              <Text mt={4} fontSize="sm" color="orange.600">
                {diagnoseHillFit(state.cohortSheet) === HILL_FIT_NO_SATURATION
                  ? "Hill ROAS model couldn't be fit — these cohorts don't show diminishing returns (ROAS does not fall as marketing spend rises), so a saturation curve can't be derived. Using the editable cumulative ROAS vector below for new marketing instead."
                  : "Hill ROAS model couldn't be fit — not enough cohorts with varying spend and valid ROAS at the same month-on-book. Using the editable cumulative ROAS vector below for new marketing instead."}
              </Text>
            ) : null}
            <CohortSheetTable
              cohortSheet={state.cohortSheet}
              expanded={state.cohortSheetExpanded}
              hillModel={hillModel}
              onToggleExpanded={() =>
                setState((s) => ({
                  ...s,
                  cohortSheetExpanded: !s.cohortSheetExpanded,
                }))
              }
            />
          </Box>
          ) : null}
        </Box>

        {/* Step 3 — Company Score KPIs */}
        <Box className="card">
          <Flex
            className={`section-header${companyScoreKpisExpanded ? '' : ' section-header-collapsed'}`}
            gap={3}
            flexWrap="wrap"
            align="center"
          >
            <span className="step-num">3</span>
            <Box flex={1} minW={0}>
              <button
                type="button"
                className="section-toggle"
                onClick={() => setCompanyScoreKpisExpanded((v) => !v)}
              >
                {companyScoreKpisExpanded ? '▼' : '▶'} Company Score KPIs
              </button>
              {companyScoreKpisExpanded ? (
                <Text className="annotation">
                  Accounting and balance-sheet inputs for the underwriting scorecard.
                </Text>
              ) : (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {uwScorecardTotals?.overallGrade
                    ? formatOverallGradeDisplay(uwScorecardTotals.overallGrade)
                    : 'Accounting and balance-sheet inputs for the underwriting scorecard.'}
                </Text>
              )}
            </Box>
          </Flex>
          {companyScoreKpisExpanded ? (
          <Box p={5}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Gross profit margin %</Text>
                  <SourceBadge label={state.gpmSource} />
                </Flex>
                <PercentInput
                  value={state.gpm}
                  onChange={(gpm) =>
                    setState((s) => ({
                      ...s,
                      gpm,
                      gpmSource: 'Manual entry',
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Revenue growth YoY %</Text>
                  <HelperTooltip content="Year-over-year revenue growth for the Revenue Growth row on the Underwriting Calculator export. Populated from accounting extract when available." />
                  <SourceBadge label={state.revenueGrowthYoySource} />
                </Flex>
                <PercentInput
                  value={state.revenueGrowthYoy}
                  onChange={(revenueGrowthYoy) =>
                    setState((s) => ({
                      ...s,
                      revenueGrowthYoy,
                      revenueGrowthYoySource: 'Manual entry',
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Monthly opex $</Text>
                  <HelperTooltip content="This opex should exclude UA." />
                  <SourceBadge label={state.opexSource} />
                </Flex>
                <DecimalInput
                  value={state.opex}
                  onChange={(opex) =>
                    setState((s) => ({
                      ...s,
                      opex,
                      opexSource: 'Manual entry',
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Net burn ($/mo)</Text>
                  <HelperTooltip content="Average absolute monthly change in cash over the last 4 complete months from accounting files." />
                  <SourceBadge label={state.netBurnSource} />
                </Flex>
                <DecimalInput
                  value={state.netBurn}
                  onChange={(netBurn) => {
                    setState((s) => ({
                      ...s,
                      netBurn,
                      netBurnSource: 'Manual entry',
                      accountingSnapshot: syncAccountingSnapshotKpis(
                        s.accountingSnapshot,
                        {
                          beginCash: s.beginCash,
                          netBurn,
                          runway: s.runway,
                          runwaySource: s.runwaySource,
                        }
                      ),
                    }));
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Beginning cash balance $</Text>
                  <SourceBadge label={state.beginCashSource} />
                </Flex>
                <DecimalInput
                  value={state.beginCash}
                  onChange={(beginCash) => {
                    setState((s) => ({
                      ...s,
                      beginCash,
                      beginCashSource: 'Manual entry',
                      accountingSnapshot: syncAccountingSnapshotKpis(
                        s.accountingSnapshot,
                        {
                          beginCash,
                          netBurn: s.netBurn,
                          runway: s.runway,
                          runwaySource: s.runwaySource,
                        }
                      ),
                    }));
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Total assets $</Text>
                  <SourceBadge label={state.totalAssetsSource} />
                </Flex>
                <DecimalInput
                  value={state.totalAssets}
                  onChange={(totalAssets) => {
                    setState((s) => ({
                      ...s,
                      totalAssets,
                      totalAssetsSource: 'Manual entry',
                      accountingSnapshot: syncAccountingSnapshotKpis(
                        s.accountingSnapshot,
                        {
                          beginCash: s.beginCash,
                          netBurn: s.netBurn,
                          runway: s.runway,
                          runwaySource: s.runwaySource,
                          totalAssets,
                          totalAssetsSource: 'Manual entry',
                          totalLiabilities: s.totalLiabilities,
                          totalLiabilitiesSource: s.totalLiabilitiesSource,
                        }
                      ),
                    }));
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Total liabilities $</Text>
                  <SourceBadge label={state.totalLiabilitiesSource} />
                </Flex>
                <DecimalInput
                  value={state.totalLiabilities}
                  onChange={(totalLiabilities) => {
                    setState((s) => ({
                      ...s,
                      totalLiabilities,
                      totalLiabilitiesSource: 'Manual entry',
                      accountingSnapshot: syncAccountingSnapshotKpis(
                        s.accountingSnapshot,
                        {
                          beginCash: s.beginCash,
                          netBurn: s.netBurn,
                          runway: s.runway,
                          runwaySource: s.runwaySource,
                          totalAssets: s.totalAssets,
                          totalAssetsSource: s.totalAssetsSource,
                          totalLiabilities,
                          totalLiabilitiesSource: 'Manual entry',
                        }
                      ),
                    }));
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Debt $</Text>
                  <HelperTooltip content="Total outstanding debt subtracted from liquid assets for Liquid Value scoring on the Underwriting Calculator export." />
                  <SourceBadge label={state.debtSource} />
                </Flex>
                <DecimalInput
                  value={state.debt}
                  onChange={(debt) =>
                    setState((s) => ({
                      ...s,
                      debt,
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">SAFE $</Text>
                  <HelperTooltip content="SAFE notes and similar convertible instruments subtracted from liabilities for the Balance Sheet UW score (assets / (liabilities − SAFE))." />
                  <SourceBadge label={state.safeSource} />
                </Flex>
                <DecimalInput
                  value={state.safe}
                  onChange={(safe) =>
                    setState((s) => ({
                      ...s,
                      safe,
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Last marketing spend $</Text>
                  <HelperTooltip content="Marketing spend on the most recent cohort row in the cohort triangle. You can override this value." />
                  <SourceBadge label={state.lastMarketingSpendSource} />
                </Flex>
                <DecimalInput
                  value={state.lastMarketingSpend}
                  onChange={(lastMarketingSpend) =>
                    setState((s) => ({
                      ...s,
                      lastMarketingSpend,
                      lastMarketingSpendSource: 'Manual entry',
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Max marketing $</Text>
                  <HelperTooltip content="Highest marketing spend where cumulative ROAS reaches at least 100% by the latest available cohort month (capped at month 12). Uses the Hill model when available. You can override this value." />
                  <SourceBadge label={state.maxMarketingRoas100Source} />
                </Flex>
                <DecimalInput
                  value={state.maxMarketingRoas100}
                  onChange={(maxMarketingRoas100) =>
                    setState((s) => ({
                      ...s,
                      maxMarketingRoas100,
                      maxMarketingRoas100Source: 'Manual entry',
                    }))
                  }
                />
              </Box>
            </SimpleGrid>

            <Text
              mt={6}
              mb={3}
              fontSize="xs"
              fontWeight={700}
              textTransform="uppercase"
              color="secondaryGray.600"
            >
              Calculated
            </Text>
            <Text fontSize="sm" color="secondaryGray.600" mb={4}>
              Derived from the parameters above and cohort data. Each value can be overridden manually.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Runway (months)</Text>
                  <HelperTooltip content="Beginning cash ÷ net burn by default. Used for the Runway row on the Underwriting Calculator export." />
                  <SourceBadge label={state.runwaySource || 'Default'} />
                </Flex>
                <DecimalInput
                  allowEmpty
                  value={state.runway}
                  onChange={(runway) => {
                    setState((s) => ({
                      ...s,
                      runway,
                      runwaySource: 'Manual entry',
                      accountingSnapshot: syncAccountingSnapshotKpis(
                        s.accountingSnapshot,
                        {
                          beginCash: s.beginCash,
                          netBurn: s.netBurn,
                          runway,
                          runwaySource: 'Manual entry',
                        }
                      ),
                    }));
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Predicted runoff $</Text>
                  <HelperTooltip content="First 12 months of predicted runoff from the cohort sheet. Used for Runoff and Liquid Value on the scorecard." />
                  <SourceBadge label={state.predictedRunoffSource || 'Default'} />
                </Flex>
                <DecimalInput
                  value={state.predictedRunoff}
                  onChange={(predictedRunoff) =>
                    setState((s) => ({
                      ...s,
                      predictedRunoff,
                      predictedRunoffSource: 'Manual entry',
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">CAC prediction</Text>
                  <HelperTooltip content="Max marketing for 100% ROAS at the latest available cohort month, divided by last cohort marketing spend." />
                  <SourceBadge label={state.cacPredictionRatioSource || 'Default'} />
                </Flex>
                <PercentInput
                  value={state.cacPredictionRatio}
                  allowEmpty
                  onChange={(cacPredictionRatio) =>
                    setState((s) => ({
                      ...s,
                      cacPredictionRatio,
                      cacPredictionRatioSource: 'Manual entry',
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Liquid value (× OPEX)</Text>
                  <HelperTooltip content="(Beginning cash + 12-month predicted runoff − debt) ÷ monthly OPEX. Used for the Liquid Value row on the scorecard." />
                  <SourceBadge label={state.liquidValueSource || 'Default'} />
                </Flex>
                <PercentInput
                  value={state.liquidValue}
                  allowEmpty
                  onChange={(liquidValue) =>
                    setState((s) => ({
                      ...s,
                      liquidValue,
                      liquidValueSource: 'Manual entry',
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Cash balance (× OPEX)</Text>
                  <HelperTooltip content="Beginning cash ÷ monthly OPEX. Used for the Cash Balance row on the company scorecard." />
                  <SourceBadge label={state.cashBalanceSource || 'Default'} />
                </Flex>
                <DecimalInput
                  allowEmpty
                  value={state.cashBalance}
                  onChange={(cashBalance) =>
                    setState((s) => ({
                      ...s,
                      cashBalance,
                      cashBalanceSource: 'Manual entry',
                    }))
                  }
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Balance sheet</Text>
                  <HelperTooltip content="Total assets ÷ (total liabilities − SAFE). Used for the Balance Sheet row on the company scorecard." />
                  <SourceBadge label={state.balanceSheetSource || 'Default'} />
                </Flex>
                <DecimalInput
                  allowEmpty
                  value={state.balanceSheet}
                  onChange={(balanceSheet) =>
                    setState((s) => ({
                      ...s,
                      balanceSheet,
                      balanceSheetSource: 'Manual entry',
                    }))
                  }
                />
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mt={6}>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Investors (1–3)</Text>
                  <HelperTooltip content="Optional manual score for the Investors row on the company scorecard. 3 = High, 2 = Moderate, 1 = Low. Leave empty to omit from the score." />
                </Flex>
                <Input
                  type="number"
                  min={1}
                  max={3}
                  step={1}
                  placeholder="—"
                  value={state.investorScore ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setState((s) => {
                      if (raw === '') {
                        return { ...s, investorScore: null };
                      }
                      const n = Number(raw);
                      if (!Number.isInteger(n) || n < 1 || n > 3) {
                        return s;
                      }
                      return { ...s, investorScore: n };
                    });
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Management (1–3)</Text>
                  <HelperTooltip content="Optional manual score for the Management row on the company scorecard. 3 = High, 2 = Moderate, 1 = Low. Leave empty to omit from the score." />
                </Flex>
                <Input
                  type="number"
                  min={1}
                  max={3}
                  step={1}
                  placeholder="—"
                  value={state.managementScore ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setState((s) => {
                      if (raw === '') {
                        return { ...s, managementScore: null };
                      }
                      const n = Number(raw);
                      if (!Number.isInteger(n) || n < 1 || n > 3) {
                        return s;
                      }
                      return { ...s, managementScore: n };
                    });
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Stress test (1–3)</Text>
                  <HelperTooltip content="Optional manual score for the Stress Testing row on the company scorecard. 3 = High, 2 = Moderate, 1 = Low. Leave empty to omit from the score." />
                </Flex>
                <Input
                  type="number"
                  min={1}
                  max={3}
                  step={1}
                  placeholder="—"
                  value={state.stressTestingScore ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setState((s) => {
                      if (raw === '') {
                        return { ...s, stressTestingScore: null };
                      }
                      const n = Number(raw);
                      if (!Number.isInteger(n) || n < 1 || n > 3) {
                        return s;
                      }
                      return { ...s, stressTestingScore: n };
                    });
                  }}
                />
              </Box>
              <Box gridColumn="1 / -1">
                <Text fontSize="sm" mb={1}>
                  Notes
                </Text>
                <Textarea
                  value={state.uwKpiNotes ?? ''}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      uwKpiNotes: e.target.value,
                    }))
                  }
                  placeholder="Optional notes for company score KPIs"
                  rows={3}
                  resize="vertical"
                />
              </Box>
            </SimpleGrid>
            <UnderwritingFinalScoreSummary
              totals={uwScorecardTotals}
              gradeThresholds={scorecardConfig?.gradeThresholds}
            />
          </Box>
          ) : null}
        </Box>

        {/* Step 4 */}
        <Box className="card">
          <Flex
            className={`section-header${marketingPlanExpanded ? '' : ' section-header-collapsed'}`}
            justify="space-between"
            flexWrap="wrap"
            gap={3}
          >
            <Flex gap={3} align="center" flex={1} minW={0}>
              <span className="step-num">4</span>
              <Box minW={0}>
                <button
                  type="button"
                  className="section-toggle"
                  onClick={() => setMarketingPlanExpanded((v) => !v)}
                >
                  {marketingPlanExpanded ? '▼' : '▶'} Marketing & funding plan
                </button>
                {!marketingPlanExpanded ? (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    ${totalMarketingSpend.toLocaleString()} total marketing spend
                  </Text>
                ) : null}
              </Box>
            </Flex>
            {marketingPlanExpanded ? (
              <Flex gap={2} align="center" flexWrap="wrap">
                <SourceBadge label={state.marketingPlanSource || 'Manual entry'} />
                {hillModel?.k ? (
                  <Text fontSize="xs" color="gray.600">
                    {`Hill avg k $${Math.round(hillModel.k).toLocaleString()}`}
                    {hillModel.kMobCount
                      ? ` · ${hillModel.kMobCount} MOBs`
                      : ''}
                  </Text>
                ) : null}
                <Text fontSize="xs" color="gray.500">
                  {planMarketingMonthCount} plan month
                  {planMarketingMonthCount === 1 ? '' : 's'} (max{' '}
                  {MAX_PLAN_MARKETING_MONTHS})
                </Text>
                <Button size="xs" onClick={removeMarketingMonth}>
                  − month
                </Button>
                <Button size="xs" onClick={addMarketingMonth}>
                  + month
                </Button>
                <Button size="xs" onClick={pasteMarketingSpend}>
                  Paste spend
                </Button>
              </Flex>
            ) : null}
          </Flex>
          {marketingPlanExpanded ? (
          <Box p={5}>
            <Box
              mb={4}
              p={4}
              border="1px solid"
              borderColor="purple.200"
              borderRadius="lg"
              bg="purple.50"
              maxW="420px"
            >
              <FacilityFeeTypeRow
                feeType={state.feeType}
                feePct={state.feePct}
                onChangeFeeType={(feeType) =>
                  setState((s) => ({ ...s, feeType }))
                }
                onChangeFeePct={(feePct) =>
                  setState((s) => ({ ...s, feePct }))
                }
              />
            </Box>
            <Flex gap={4} mb={marketingPlanExpanded ? 4 : 2} flexWrap="wrap" align="flex-end">
              <Box>
                <Text fontSize="sm" mb={1}>
                  Commitment (months)
                </Text>
                <Input
                  type="number"
                  size="sm"
                  w="100px"
                  textAlign="right"
                  min={1}
                  max={planMarketingMonthCount}
                  value={
                    state.commitmentMonths == null || state.commitmentMonths === ''
                      ? planMarketingMonthCount
                      : state.commitmentMonths
                  }
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    setState((s) => {
                      const planLength = s.marketing.length;
                      if (raw === '') {
                        return { ...s, commitmentMonths: null };
                      }
                      const val = Math.max(
                        1,
                        Math.min(planLength, Math.floor(Number(raw) || 1))
                      );
                      return {
                        ...s,
                        commitmentMonths:
                          val >= planLength ? null : val,
                      };
                    });
                  }}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>
                  Max Funding %
                </Text>
                <Input
                  type="number"
                  size="sm"
                  w="100px"
                  textAlign="right"
                  isDisabled={state.marketingPlanManualMode}
                  value={state.defaultFundingPct ?? DEFAULT_FUNDING_PCT}
                  onChange={(e) => {
                    const val = Number(e.target.value) || 0;
                    setState((s) => {
                      const limit = resolveFundingCommitmentMonths(s);
                      return {
                        ...s,
                        defaultFundingPct: val,
                        fundingPct: s.fundingPct.map((pct, i) =>
                          i < limit ? val : pct
                        ),
                      };
                    });
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={0} mb={1}>
                  <Text fontSize="sm">Max Revenue share %</Text>
                  <HelperTooltip content={PRO_RATA_REV_SHARE_TOOLTIP} />
                </Flex>
                <Input
                  type="number"
                  size="sm"
                  w="100px"
                  textAlign="right"
                  placeholder="Pro-rata"
                  isDisabled={state.marketingPlanManualMode}
                  value={
                    state.defaultRevSharePct != null &&
                    state.defaultRevSharePct !== ''
                      ? state.defaultRevSharePct
                      : ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    setState((s) => {
                      const limit = resolveFundingCommitmentMonths(s);
                      if (raw === '') {
                        return {
                          ...s,
                          defaultRevSharePct: null,
                          revSharePct: s.revSharePct.map((pct, i) =>
                            i < limit ? null : pct
                          ),
                        };
                      }
                      const val = Number(raw) || 0;
                      return {
                        ...s,
                        defaultRevSharePct: val,
                        revSharePct: s.revSharePct.map((pct, i) =>
                          i < limit ? val : pct
                        ),
                      };
                    });
                  }}
                />
              </Box>
              <Box>
                <Flex align="center" gap={0} mb={1}>
                  <Text fontSize="sm">Max exposure ($)</Text>
                  <HelperTooltip content="Required. Ending Twelve balance must not exceed this amount at any month. Default is $1,000,000. Changing this also fills facility size. In manual row mode, override the cap per row." />
                </Flex>
                <Input
                  aria-label="Max exposure ($)"
                  type="text"
                  inputMode="numeric"
                  size="sm"
                  w="140px"
                  textAlign="right"
                  isDisabled={state.marketingPlanManualMode}
                  value={fmtIntegerInput(resolveRequiredMaxExposure(state.maxExposure))}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, '').trim();
                    const parsed = Math.max(0, parseIntegerInput(raw));
                    const maxExposure = parsed > 0 ? parsed : DEFAULT_MAX_EXPOSURE;
                    setState((s) => stateWithMaxExposure(s, maxExposure));
                  }}
                />
              </Box>
              {!state.marketingPlanManualMode ? (
              <Box>
                <Flex align="center" gap={0} mb={1}>
                  <Text fontSize="sm">Max monthly draw ($)</Text>
                  <HelperTooltip content="Optional default. Maximum Twelve draw for each cohort’s funding month. Override per row in the table below." />
                </Flex>
                <Input
                  type="text"
                  inputMode="numeric"
                  size="sm"
                  w="140px"
                  textAlign="right"
                  placeholder="Optional"
                  value={
                    state.maxMonthlyDraw != null
                      ? fmtIntegerInput(state.maxMonthlyDraw)
                      : ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, '').trim();
                    setState((s) => ({
                      ...s,
                      maxMonthlyDraw:
                        raw === '' ? null : Math.max(0, parseIntegerInput(raw)),
                    }));
                  }}
                />
              </Box>
              ) : null}
              {!state.marketingPlanManualMode ? (
              <Box>
                <Flex align="center" gap={0} mb={1}>
                  <Text fontSize="sm">Max draw increase (%)</Text>
                  <HelperTooltip content="Optional. Maximum month-over-month increase in draw vs the prior month’s draw. Violations are highlighted on the Draw row with the allowed draw amount." />
                </Flex>
                <Input
                  type="number"
                  min={0}
                  size="sm"
                  w="100px"
                  textAlign="right"
                  placeholder="Optional"
                  value={state.maxDrawIncreasePct ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    setState((s) => ({
                      ...s,
                      maxDrawIncreasePct:
                        raw === '' ? null : Math.max(0, Number(raw) || 0),
                    }));
                  }}
                />
              </Box>
              ) : null}
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="sm">Manual row settings</Text>
                  <HelperTooltip content={MANUAL_PLAN_MODE_TOOLTIP} />
                </Flex>
                <Switch
                  size="sm"
                  isChecked={Boolean(state.marketingPlanManualMode)}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setState((s) => {
                      if (!enabled) {
                        return disableMarketingPlanManualMode(s);
                      }
                      return {
                        ...s,
                        marketingPlanManualMode: true,
                        planDrawAmount: seedPlanDrawAmountFromFundingPct(s),
                      };
                    });
                  }}
                />
              </Box>
            </Flex>
            <MarketingPlanMonthsRow
              state={state}
              setState={setState}
              planMonths={planMonths}
              spendRoasVectors={spendRoasVectors}
              useHillRoas={useHillRoas}
              editableFirstMonth
              allowPasteSpend
            />
          </Box>
          ) : null}
        </Box>

        {/* Step 5 */}
        <Box className="card">
          <Flex className="section-header" gap={3} align="center">
            <span className="step-num">5</span>
            <Text fontWeight="semibold">Facility &amp; repayment terms</Text>
            <HelperTooltip content={NUMBER_OF_REPAYMENTS_TOOLTIP} />
          </Flex>
          <Box p={5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 10 }}>
              <SimpleGrid columns={1} spacing={4}>
                <Box>
                  <Flex align="center" gap={0} mb={1}>
                    <Text fontSize="sm">Facility size ($)</Text>
                    <HelperTooltip content={FACILITY_SIZE_TOOLTIP} />
                  </Flex>
                  <Input
                    aria-label="Facility size ($)"
                    type="text"
                    inputMode="numeric"
                    value={fmtIntegerInput(resolveFacilitySize(state))}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, '').trim();
                      const parsed = Math.max(0, parseIntegerInput(raw));
                      setState((s) => ({
                        ...s,
                        facilitySize: parsed > 0 ? parsed : 0,
                      }));
                    }}
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Facility fee (%)
                  </Text>
                  <Input
                    type="number"
                    value={state.facilityFeePct}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        facilityFeePct: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Facility fee amount ($)
                  </Text>
                  <Input
                    isReadOnly
                    value={`$${facilityFeeAmount.toLocaleString()}`}
                  />
                </Box>
                <Box>
                  <Flex align="center" gap={0} mb={1}>
                    <Text fontSize="sm">Facility fee installments</Text>
                    <HelperTooltip content="Number of equal monthly payments applied to the facility fee. The first installment is due in the same month as the first cohort repayment (after the repayment grace period). Rounding remainder is applied to the final installment. The unpaid portion does not accrue XIRR fee." />
                  </Flex>
                  <Select
                    value={String(facilityFeeInstallmentCount)}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        facilityFeeInstallments: Math.max(
                          1,
                          Math.min(
                            MAX_FACILITY_FEE_INSTALLMENTS,
                            Number(e.target.value)
                          )
                        ),
                      }))
                    }
                  >
                    {Array.from(
                      { length: MAX_FACILITY_FEE_INSTALLMENTS },
                      (_, i) => i + 1
                    ).map((count) => (
                      <option key={count} value={count}>
                        {count}
                        {count === 1 ? ' — single payment (default)' : ''}
                      </option>
                    ))}
                  </Select>
                  {facilityFeeAmount > 0 && facilityFeeInstallmentCount > 1 ? (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      ≈ ${facilityFeePerInstallment.toLocaleString()} per
                      installment
                      {facilityFeeAmount % facilityFeeInstallmentCount !== 0
                        ? ' (last installment includes rounding)'
                        : ''}
                    </Text>
                  ) : null}
                </Box>
              </SimpleGrid>
              <SimpleGrid columns={1} spacing={4}>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Number of repayments
                  </Text>
                  <Input
                    type="number"
                    min={1}
                    value={state.numberOfRepayments ?? effectiveRoasLength}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      setState((s) => ({
                        ...s,
                        numberOfRepayments:
                          raw === ''
                            ? null
                            : Math.max(1, Math.floor(Number(raw) || 1)),
                      }));
                    }}
                  />
                  <Text fontSize="xs" color="gray.400">
                    Defaults to ROAS length when blank ({effectiveRoasLength})
                  </Text>
                </Box>
                <FacilityGracePeriodsRow
                  gracePeriod={state.gracePeriod}
                  marketingGracePeriod={state.marketingGracePeriod}
                  onChangeGracePeriod={(gracePeriod) =>
                    setState((s) => ({ ...s, gracePeriod }))
                  }
                  onChangeMarketingGracePeriod={(marketingGracePeriod) =>
                    setState((s) => ({ ...s, marketingGracePeriod }))
                  }
                />
                <Box>
                  <Flex align="center" gap={0} mb={1}>
                    <Text fontSize="sm">Runoff exposure cohort count</Text>
                    <HelperTooltip content="When set, adds a cohort-window runoff and runoff exposure in results. At each month, counts Twelve-funded plan cohorts active through that month; if the count is below this number, fills the window with the most recent unfunded plan cohorts (marketing or revenue, no draw), then old cohort-sheet rows from the cohort tab." />
                  </Flex>
                  <Input
                    type="number"
                    min={1}
                    value={state.runoffExposureCohortCount ?? ''}
                    placeholder="Off"
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      setState((s) => ({
                        ...s,
                        runoffExposureCohortCount:
                          raw === ''
                            ? null
                            : Math.max(1, Math.floor(Number(raw) || 1)),
                      }));
                    }}
                  />
                </Box>
              </SimpleGrid>
            </SimpleGrid>
          </Box>
        </Box>

        {/* Step 6 — Scenarios */}
        <ScenarioSettingsSection
          scenarios={state.scenarios}
          onChange={updateScenario}
        />

        {resultsVisible && calcResult && (
          <Box ref={resultsRef}>
            <ScenarioResultsSection
              scenarioMap={{
                base: calcResult,
                bull: scenarioResults?.bull ?? null,
                bear: scenarioResults?.bear ?? null,
              }}
              calcTimestamp={calcTimestamp}
              state={state}
              incomeExpanded={incomeExpanded}
              onToggleIncome={() => setIncomeExpanded((v) => !v)}
              exposureGradeLimits={exposureGradeLimits}
              onDownloadExcel={handleDownloadPlExcel}
            />
          </Box>
        )}
      </Box>

      <Box className="sticky-calc-bar">
        <Flex maxW="1400px" mx="auto" align="center" justify="space-between">
          <Text fontSize="sm" color="gray.500">
            {calcStatus}
          </Text>
          <Flex gap={2} align="center">
            <Flex align="center" gap={1}>
              <Text fontSize="sm" whiteSpace="nowrap">
                Auto-fix funding
              </Text>
              <HelperTooltip content={AUTO_FIX_FUNDING_TOOLTIP} placement="top" />
              <Switch
                size="sm"
                colorScheme="purple"
                isChecked={Boolean(state.autoFixFundingEnabled)}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    autoFixFundingEnabled: e.target.checked,
                  }))
                }
              />
            </Flex>
            <Button variant="outline" onClick={onReset}>
              Clear
            </Button>
            <Button
              variant="outline"
              colorScheme="purple"
              onClick={handleSaveToDrive}
              isDisabled={!excelDownloadEnabled}
              isLoading={savingToDrive}
            >
              Save to Drive
            </Button>
            <Button
              colorScheme="purple"
              onClick={onCalculate}
              isLoading={calculating}
            >
              Calculate
            </Button>
          </Flex>
        </Flex>
      </Box>

      <AlertDialog
        isOpen={isSaveConfirmOpen}
        leastDestructiveRef={saveCancelRef}
        onClose={closeSaveConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Overwrite saved model?
            </AlertDialogHeader>
            <AlertDialogBody>
              Overwrite &ldquo;{saveOverwriteTarget}&rdquo;? Your current inputs
              and results will replace the saved version.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={saveCancelRef} onClick={closeSaveConfirm}>
                Cancel
              </Button>
              <Button colorScheme="purple" onClick={onConfirmSave} ml={3}>
                Overwrite
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default QuickCashflowModelClassic;
